/**
 * To write a paper on a paid plan, the model is picked for each request: the choice is remembered in
 * this browser and sent with every writing action (section briefs and texts, edits, reviews, fixes,
 * responses). A bigger model uses more credits, in proportion to its price.
 */
import { useEffect, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { AI_ACTION_CREDITS, WRITING_AI_ACTIONS } from "@brainlog/shared";
import { get } from "@/lib/api";
import { cn } from "@/lib/utils";

interface WritingModels {
  offered: boolean;
  ownProvider: boolean;
  freePlan: boolean;
  choices: { model: string; label: string; factor: number }[];
  default: string | null;
}

const KEY = "brainlog-writing-model";
let models: WritingModels | null = null;
let choice: string | null = (() => {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
})();
const listeners = new Set<() => void>();
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

const isWriting = (action: string) => (WRITING_AI_ACTIONS as readonly string[]).includes(action);

/** The model to send with an action (undefined: not a writing action, or nothing to pick). */
export function writingModelFor(action: string): string | undefined {
  if (!isWriting(action) || !models?.offered) return undefined;
  return (models.choices.some((c) => c.model === choice) ? choice : models.default) ?? undefined;
}

function setChoice(model: string) {
  choice = model;
  try {
    localStorage.setItem(KEY, model);
  } catch {
    /* not remembered in this browser */
  }
  for (const l of listeners) l();
}

export function useWritingModels() {
  const q = useQuery({ queryKey: ["ai", "writing-models"], queryFn: () => get<WritingModels>("/api/ai/writing-models"), staleTime: 5 * 60_000 });
  useEffect(() => {
    if (q.data) {
      models = q.data;
      for (const l of listeners) l();
    }
  }, [q.data]);
  return q.data;
}

/** The model picker of the writing dialogs, with the credits the action will use with each model. */
export function WritingModelPicker({ action, className }: { action: string; className?: string }) {
  const data = useWritingModels();
  const current = useSyncExternalStore(subscribe, () => writingModelFor(action));
  if (!data || data.ownProvider) return null;
  if (!data.offered) {
    return data.freePlan ? (
      <p className={cn("text-xs text-muted-foreground", className)}>Written with the standard AI model. Choosing the best writing models comes with the Researcher plans.</p>
    ) : null;
  }
  const base = AI_ACTION_CREDITS[action] ?? 1;
  return (
    <fieldset className={cn("space-y-1", className)}>
      <legend className="text-xs font-medium text-muted-foreground">AI model for this request</legend>
      <div role="radiogroup" aria-label="AI model" className="flex flex-wrap gap-1.5">
        {data.choices.map((c) => (
          <button
            key={c.model}
            type="button"
            role="radio"
            aria-checked={current === c.model}
            onClick={() => setChoice(c.model)}
            className={cn("rounded-md border px-2.5 py-1 text-left text-xs hover:bg-accent/50", current === c.model && "border-primary bg-accent/60")}
          >
            <span className="font-medium">{c.label}</span>
            <span className="text-muted-foreground">
              {" "}
              · {base * c.factor} credit{base * c.factor === 1 ? "" : "s"}
              {c.model === data.default ? " · default" : ""}
            </span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}
